import { useNavigate, useSearch } from "@tanstack/react-router";
import { Check, Flag, History, Lock, TriangleAlert } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import {
  DataTable,
  type DataTableColumn,
  type DataTableSort,
} from "@/components/cinch/DataTable/DataTable";
import { DeltaCell } from "@/components/cinch/DeltaCell";
import {
  ActiveFilterPill,
  CheckboxList,
  FilterBar,
  FilterChip,
  FilterClearAll,
  FilterSearch,
} from "@/components/cinch/FilterBar";
import { IconButton } from "@/components/cinch/IconButton";
import { KpiTile } from "@/components/cinch/KpiTile";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";

import type { ReconStepSearch } from "..";
import {
  monthLabel,
  useReplacements,
  useReviewReplacement,
  SUPPRESSION_LABELS,
  type ReplacementRow,
  type ReviewStatus,
} from "../api";
import { fmtCur, fmtCur2, fmtDate } from "../../renewals/format";
import { ReplacementDrawer } from "./ReplacementDrawer";

const REVIEW_LABELS: { value: ReviewStatus; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "flagged", label: "Flagged" },
];

/** Suppression states that mean something is actually wrong, as opposed to
 *  "there was nothing to check". */
const FAILING = new Set([
  "replaced_still_open",
  "amendment_still_open",
  "handoff_gap",
  "handoff_overlap",
]);

/** R-08 worklist: EVERY replacement contract, open until someone has confirmed
 *  what it replaced and what it netted.
 *
 *  That verification is durable and not period-scoped, so this is a burndown
 *  rather than a monthly slice — the first pass is the whole history (42 today),
 *  and later months only add to it. Nothing is quietly dropped because its month
 *  closed, which is how a stranded contract went unnoticed for nine months.
 *
 *  The backlog never blocks the close; only an open flag does. Live from
 *  Snowflake, with a per-row live Cinch check for anything the reviewer just
 *  changed, since the mart is up to a day behind. */
export function ReplacementSuppressionPanel({ period }: { period: string }) {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as ReconStepSearch;
  const setSearch = (next: Partial<ReconStepSearch>) =>
    navigate({
      to: "/reconciliation/$period/$stepKey",
      params: { period, stepKey: "R-08" },
      search: (prev: ReconStepSearch) => ({ ...prev, ...next }),
      replace: true,
    });

  const { data, isLoading, error } = useReplacements(period);
  const review = useReviewReplacement(period);
  const [sort, setSort] = useState<DataTableSort | null>({
    key: "sign_date",
    dir: "desc",
  });
  const [flagIntent, setFlagIntent] = useState(false);

  const all = useMemo(() => data?.replacements ?? [], [data]);
  const reviewFilter = useMemo(
    () => (search.review ?? "").split(",").filter(Boolean) as ReviewStatus[],
    [search.review],
  );
  const q = search.q ?? "";

  const rows = useMemo(() => {
    let out = all;
    const needle = q.trim().toLowerCase();
    if (needle) {
      out = out.filter((r) =>
        `${r.company_name ?? ""} ${r.account_name ?? ""} ${r.contract_type_name ?? ""}`
          .toLowerCase()
          .includes(needle),
      );
    }
    if (reviewFilter.length) {
      out = out.filter((r) => reviewFilter.includes(r.review_status));
    }
    if (search.mismatch) out = out.filter((r) => FAILING.has(r.suppression));
    // `mrr` is the shared boolean slot in ReconStepSearch; here it means
    // "awaiting verification only".
    if (search.mrr) out = out.filter((r) => r.is_open);
    if (sort) {
      const dir = sort.dir === "asc" ? 1 : -1;
      out = [...out].sort((a, b) => {
        const va = value(a, sort.key);
        const vb = value(b, sort.key);
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        return va < vb ? -dir : va > vb ? dir : 0;
      });
    }
    return out;
  }, [all, q, reviewFilter, search.mismatch, search.mrr, sort]);

  const summary = data?.summary;
  const locked = data?.locked ?? false;
  const canAct = (data?.can_act ?? false) && !locked;
  const supersededCount = all.filter((r) => r.review_superseded).length;
  const stranded = all.filter((r) => r.suppression === "replaced_still_open");
  const strandedArr = stranded.reduce(
    (s, r) => s + (r.replaced_stack_mrr ?? 0) * 12,
    0,
  );
  const curLabel = monthLabel(period);
  const drawerRow = useMemo(
    () => all.find((r) => r.contract_id === search.contract) ?? null,
    [all, search.contract],
  );

  function quickReview(r: ReplacementRow, status: ReviewStatus) {
    review.mutate(
      { contractId: r.contract_id, status },
      {
        onError: (e) =>
          toast.error("Couldn't save that review", {
            description: (e as Error).message,
          }),
      },
    );
  }

  function openDrawer(r: ReplacementRow, flag: boolean, expanded = false) {
    setFlagIntent(flag);
    setSearch({ contract: r.contract_id, expanded: expanded || undefined });
  }

  const columns: DataTableColumn<ReplacementRow>[] = [
    {
      key: "account_name",
      header: "Account",
      sortable: true,
      className: "max-w-[260px]",
      render: (r) => (
        <button
          type="button"
          className="block max-w-full text-left"
          title={`${r.account_name ?? "this account"} — open the full comparison`}
          onClick={(e) => {
            e.stopPropagation();
            openDrawer(r, false, true);
          }}
        >
          <span className="block truncate text-sm font-semibold text-brand hover:underline">
            {r.account_name ?? r.company_name ?? "—"}
          </span>
          {r.company_name && r.company_name !== r.account_name && (
            <span className="block truncate text-xs text-muted-foreground">
              {r.company_name}
            </span>
          )}
        </button>
      ),
    },
    {
      key: "sign_date",
      header: "Signed",
      sortable: true,
      render: (r) => (
        <span className="whitespace-nowrap">
          {fmtDate(r.sign_date)}
          {!r.in_period && r.is_open && (
            <span
              className="ml-1.5 text-[10px] font-medium uppercase tracking-wide text-warning"
              title="Signed in an earlier month, still awaiting verification. Doesn't count toward this month's bookings."
            >
              backlog
            </span>
          )}
        </span>
      ),
    },
    {
      key: "replaced_stack_mrr",
      header: "Replaced MRR",
      numeric: true,
      sortable: true,
      render: (r) =>
        r.replaced_stack_mrr == null ? (
          <span
            className="text-muted-foreground"
            title="No replaced contract linked — nothing to compare against"
          >
            —
          </span>
        ) : (
          <span title={amendmentHint(r)}>
            {fmtCur2(r.replaced_stack_mrr)}
            {r.amendment_mrr > 0 && <sup className="ml-0.5 text-warning">+</sup>}
          </span>
        ),
    },
    {
      key: "replaced_stack_arr",
      header: "Replaced ARR",
      numeric: true,
      sortable: true,
      render: (r) =>
        r.replaced_stack_arr == null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          fmtCur(r.replaced_stack_arr)
        ),
    },
    {
      key: "prior_billed_arr",
      header: "Last billed ARR",
      numeric: true,
      sortable: true,
      render: (r) =>
        r.prior_billed_arr == null ? (
          <span
            className="text-muted-foreground italic"
            title="No billing history for the month before signing — unknown, not zero"
          >
            unknown
          </span>
        ) : (
          <span title={`Billed in ${r.prior_period ?? "the prior month"}`}>
            {fmtCur(r.prior_billed_arr)}
          </span>
        ),
    },
    {
      key: "replacement_mrr",
      header: "New MRR",
      numeric: true,
      sortable: true,
      render: (r) => fmtCur2(r.replacement_mrr),
    },
    {
      key: "replacement_arr",
      header: "New ARR",
      numeric: true,
      sortable: true,
      render: (r) => fmtCur(r.replacement_arr),
    },
    {
      key: "net_change_arr",
      header: "Net change ARR",
      numeric: true,
      sortable: true,
      className: "w-40",
      render: (r) =>
        r.valued ? (
          <span title={baselineHint(r)}>
            <DeltaCell value={r.net_change_arr} chip emptyLabel="no change" />
          </span>
        ) : (
          <span
            className="text-xs font-medium text-muted-foreground"
            title="No baseline can be computed for this instrument — a purchase order's account is the reseller, so comparing it to account-level billing is meaningless"
          >
            unvalued
          </span>
        ),
    },
    {
      key: "suppression",
      header: "Suppression",
      sortable: true,
      render: (r) => (
        <span className="inline-flex items-center gap-1.5">
          {FAILING.has(r.suppression) && (
            <TriangleAlert className="h-3.5 w-3.5 flex-shrink-0 text-danger" />
          )}
          <span
            className={
              FAILING.has(r.suppression)
                ? "text-sm font-medium text-danger"
                : r.suppression === "unverifiable"
                  ? "text-sm text-muted-foreground"
                  : "text-sm text-success"
            }
            title={suppressionHint(r)}
          >
            {SUPPRESSION_LABELS[r.suppression]}
          </span>
        </span>
      ),
    },
    {
      key: "verified",
      header: "Verified",
      sortable: true,
      render: (r) =>
        r.is_open ? (
          <span
            className="text-xs text-muted-foreground"
            title={
              r.replaced_contract_id
                ? "Not yet settled — open the drawer to confirm what it replaced and what it netted"
                : "Can't be settled until the superseded contract is linked in Cinch"
            }
          >
            {r.verification?.stale
              ? "reopened"
              : r.replaced_contract_id
                ? "open"
                : "needs link"}
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-1 text-xs font-medium text-success"
            title={`Settled by ${r.verification?.verified_by_name ?? "—"}${
              r.verification?.verified_in_period
                ? ` in the ${r.verification.verified_in_period} close`
                : ""
            }`}
          >
            <Check className="h-3.5 w-3.5" />
            {r.verification?.status === "no_replacement" ? "replaces nothing" : "verified"}
          </span>
        ),
    },
    {
      key: "review",
      header: "Review",
      sortable: true,
      align: "right",
      className: "w-40",
      render: (r) => (
        <span className="inline-flex items-center justify-end gap-1.5">
          {r.review_superseded && (
            <History
              className="h-3.5 w-3.5 text-warning"
              aria-label="Review lapsed — the numbers changed after it was reviewed"
            />
          )}
          <StatusBadge value={r.review_status} />
          {canAct && (
            <>
              <IconButton
                title="Approve — this replacement is netted and suppressed correctly"
                active={r.review_status === "approved"}
                activeClassName="text-success"
                hoverClassName="hover:bg-success-light hover:text-success"
                disabled={review.isPending}
                onClick={() => quickReview(r, "approved")}
              >
                <Check className="h-3.5 w-3.5" />
              </IconButton>
              <IconButton
                title="Flag — something's wrong (opens notes); blocks completing this step"
                active={r.review_status === "flagged"}
                activeClassName="text-warning"
                hoverClassName="hover:bg-warning-light hover:text-warning"
                onClick={() => openDrawer(r, true)}
              >
                <Flag className="h-3.5 w-3.5" />
              </IconButton>
            </>
          )}
        </span>
      ),
    },
  ];

  if (isLoading && !data) return <Card className="h-64 animate-pulse" />;

  return (
    <div className="flex flex-col gap-4">
      {data?.error && (
        <Alert variant="danger">
          <AlertTitle>Couldn't read replacements</AlertTitle>
          <AlertDescription>{data.error}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-muted-foreground">
          {locked && data?.frozen_at
            ? `Frozen ${new Date(data.frozen_at).toLocaleString()} when the step was signed off`
            : "Every replacement, open until verified. Snowflake is up to a day behind — the drawer can check Cinch directly for anything you just changed."}
        </div>
        {locked && (
          <span className="flex items-center gap-1 text-xs font-medium text-success">
            <Lock className="h-3.5 w-3.5" /> Signed off — frozen
          </span>
        )}
      </div>

      {stranded.length > 0 && (
        <Alert variant="danger">
          <AlertTitle>
            {stranded.length} superseded contract
            {stranded.length === 1 ? " was" : "s were"} never closed
          </AlertTitle>
          <AlertDescription>
            Both the replacement and the contract it replaced are still open on the
            account, so booked ARR counts them twice — <b>{fmtCur(strandedArr)}</b>{" "}
            in total. Close the superseded contract in Cinch; the row updates as
            soon as you check, without waiting for the mart.
          </AlertDescription>
        </Alert>
      )}

      {supersededCount > 0 && (
        <Alert variant="warning">
          <AlertTitle>
            {supersededCount} review{supersededCount === 1 ? "" : "s"} lapsed
          </AlertTitle>
          <AlertDescription>
            The numbers changed after {supersededCount === 1 ? "it was" : "they were"}{" "}
            reviewed, so the earlier verdict no longer describes{" "}
            {supersededCount === 1 ? "it" : "them"}. The notes were kept —
            re-review to clear.
          </AlertDescription>
        </Alert>
      )}

      <div
        className="grid gap-3"
        // 172px rather than the usual 190: this step carries seven tiles (the
        // burndown pair, the month, and three exception counts), and at 190 the
        // last one wrapped alone onto a second row.
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(172px, 1fr))" }}
      >
        <KpiTile
          label="Awaiting verification"
          value={String(summary?.open_count ?? 0)}
          tone={summary?.open_count ? "warning" : "positive"}
          sub={`of ${summary?.total_count ?? 0} replacements ever`}
          info="A replacement stays open until someone has confirmed what it replaced and what it netted. That record is durable, so this burns down once — the first pass is the whole history."
          onClick={() => setSearch({ mrr: search.mrr ? undefined : true })}
          selected={!!search.mrr}
        />
        <KpiTile
          label="Verified"
          value={String(
            (summary?.verified_count ?? 0) + (summary?.no_replacement_count ?? 0),
          )}
          tone="positive"
          sub={
            summary?.no_replacement_count
              ? `${summary.no_replacement_count} replace nothing`
              : "settled for good"
          }
        />
        <KpiTile
          label={`Signed · ${curLabel}`}
          value={String(summary?.replacement_count ?? 0)}
          sub={
            summary?.open_in_month_count
              ? `${summary.open_in_month_count} still to verify`
              : "all verified"
          }
        />
        <KpiTile
          label="Net change ARR"
          value={fmtCur(summary?.net_change_arr ?? 0)}
          tone={(summary?.net_change_arr ?? 0) < 0 ? "warning" : "positive"}
          info="What this month's replacements added over what each account was already paying — the month's replacement bookings. Uses the verified figure where one is settled, otherwise the computed proposal."
        />
        <KpiTile
          label="No link in Cinch"
          value={String(summary?.missing_link_count ?? 0)}
          tone={summary?.missing_link_count ? "warning" : "positive"}
          sub="can't be verified yet"
          info="Only 'Replacement Contract' carries a replaced-contract link in Cinch. The rest need one established there before they can be settled — the drawer suggests candidates."
        />
        <KpiTile
          label="Suppression failures"
          value={String(summary?.suppression_failure_count ?? 0)}
          tone={summary?.suppression_failure_count ? "danger" : "positive"}
          info="A replaced contract left open, an upsell still running on it, or a gap/overlap at the handoff. Each one means booked ARR is wrong."
          onClick={() => setSearch({ mismatch: search.mismatch ? undefined : true })}
          selected={!!search.mismatch}
        />
        <KpiTile
          label="Flagged"
          value={String(summary?.flagged_count ?? 0)}
          tone={summary?.flagged_count ? "danger" : "positive"}
          info="An open flag blocks completing this step — completing it is the attestation that every restructure this month is netted out and suppressed."
          onClick={() =>
            setSearch({ review: search.review === "flagged" ? undefined : "flagged" })
          }
          selected={search.review === "flagged"}
        />
      </div>

      <FilterBar>
        <FilterSearch
          placeholder="Search account, company, type…"
          value={q}
          onChange={(v) => setSearch({ q: v || undefined })}
        />
        <FilterChip label="Review" count={reviewFilter.length}>
          <CheckboxList
            options={REVIEW_LABELS}
            selected={reviewFilter}
            onChange={(v) => setSearch({ review: v.length ? v.join(",") : undefined })}
          />
        </FilterChip>
        {reviewFilter.map((r) => (
          <ActiveFilterPill
            key={r}
            label={REVIEW_LABELS.find((o) => o.value === r)?.label ?? r}
            onRemove={() =>
              setSearch({
                review: reviewFilter.filter((v) => v !== r).join(",") || undefined,
              })
            }
          />
        ))}
        {search.mismatch && (
          <ActiveFilterPill
            label="Suppression failures"
            onRemove={() => setSearch({ mismatch: undefined })}
          />
        )}
        {(reviewFilter.length > 0 || q || search.mismatch) && (
          <FilterClearAll
            onClick={() =>
              setSearch({ review: undefined, q: undefined, mismatch: undefined })
            }
          />
        )}
      </FilterBar>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.contract_id}
        sort={sort}
        onSortChange={setSort}
        loading={isLoading}
        error={error ? String(error) : null}
        emptyMessage={
          all.length
            ? "No replacements match."
            : `No replacements were signed in ${curLabel}, and nothing is carried in.`
        }
        onRowClick={(r) => openDrawer(r, false)}
        selectedKey={search.contract ?? null}
        containerClassName="max-h-[calc(100vh-16rem)]"
      />

      <p className="text-xs text-muted-foreground">
        Every replacement stays on this list until someone confirms what it
        replaced and what it netted — that record is durable, so the backlog burns
        down once and new months only add to it. Net change is the new contract's
        MRR less what the account was actually billed in the period before it was
        signed, so revenue the customer was already handing over is never counted
        as new. Click an account to settle it — the drawer shows the difference
        item by item.
      </p>

      <ReplacementDrawer
        period={period}
        row={drawerRow}
        canAct={canAct}
        expanded={!!search.expanded}
        presetFlag={flagIntent}
        onExpandedChange={(v) => setSearch({ expanded: v || undefined })}
        onClose={() => {
          setFlagIntent(false);
          setSearch({ contract: undefined, expanded: undefined });
        }}
      />
    </div>
  );
}

function amendmentHint(r: ReplacementRow): string | undefined {
  if (r.amendment_mrr <= 0) return undefined;
  return `Includes ${r.amendment_count} upsell${r.amendment_count === 1 ? "" : "s"} amending the replaced contract (${fmtCur2(r.amendment_mrr)}/mo) — they closed with it, so they're part of what the replacement has to beat.`;
}

function baselineHint(r: ReplacementRow): string {
  return `${fmtCur2(r.replacement_mrr)} new − ${fmtCur2(r.baseline_mrr ?? 0)} baseline, which is what the account was actually billed in ${r.prior_period ?? "the period before signing"}.`;
}

function suppressionHint(r: ReplacementRow): string {
  switch (r.suppression) {
    case "ok":
      return `The replaced contract ended ${fmtDate(r.replaced_eff_through)}, the day before this one starts.`;
    case "replaced_still_open":
      return "The contract this replaces has no end date and wasn't cancelled — both are open on the account, so booked ARR counts it twice.";
    case "amendment_still_open":
      return "An upsell amending the replaced contract was left running past the handoff.";
    case "handoff_gap":
      return `The replaced contract ended ${r.handoff_gap_days} days before this one starts — an unbilled window.`;
    case "handoff_overlap":
      return "The replaced contract runs past the day this one starts — an overlap, billed twice.";
    default:
      return "No replaced contract is linked, so there's nothing to check. True of every replacement type except 'Replacement Contract'.";
  }
}

function value(r: ReplacementRow, key: string): string | number | null {
  switch (key) {
    case "account_name":
      return r.account_name?.toLowerCase() ?? null;
    case "sign_date":
      return r.sign_date;
    case "replaced_stack_mrr":
    case "replaced_stack_arr":
      return r.replaced_stack_mrr;
    case "prior_billed_arr":
      return r.prior_billed_mrr;
    case "replacement_mrr":
    case "replacement_arr":
      return r.replacement_mrr;
    case "net_change_arr":
      return r.net_change_mrr;
    case "suppression":
      return r.suppression;
    case "verified":
      return r.is_open ? 0 : 1;
    case "review":
      return r.review_status;
    default:
      return null;
  }
}
